import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

export const DEFAULT_PAGE_TITLE = "Primum Cloud Services";
const TITLE_SUFFIX = "Primum Cloud";

type PageTitleEntry = {
    id: string;
    title: string;
    priority: number;
    updatedAt: number;
};

type PageTitleContextValue = {
    setTitle: (entry: Omit<PageTitleEntry, "updatedAt">) => void;
    clearTitle: (id: string) => void;
};

const PageTitleContext = createContext<PageTitleContextValue | null>(null);

const formatTitle = (title?: string) => {
    const normalizedTitle = title?.trim();
    return normalizedTitle ? `${normalizedTitle} | ${TITLE_SUFFIX}` : DEFAULT_PAGE_TITLE;
};

export const PageTitleProvider = ({ children }: { children: ReactNode }) => {
    const [entries, setEntries] = useState<Record<string, PageTitleEntry>>({});
    const revisionRef = useRef(0);

    const setTitle = useCallback((entry: Omit<PageTitleEntry, "updatedAt">) => {
        const title = entry.title.trim();

        if (!title) {
            setEntries((current) => {
                const { [entry.id]: _removed, ...nextEntries } = current;
                return nextEntries;
            });
            return;
        }

        setEntries((current) => ({
            ...current,
            [entry.id]: {
                ...entry,
                title,
                updatedAt: ++revisionRef.current,
            },
        }));
    }, []);

    const clearTitle = useCallback((id: string) => {
        setEntries((current) => {
            if (!current[id]) {
                return current;
            }

            const { [id]: _removed, ...nextEntries } = current;
            return nextEntries;
        });
    }, []);

    const activeTitle = useMemo(() => {
        const activeEntry = Object.values(entries)
            .sort((left, right) => right.priority - left.priority || right.updatedAt - left.updatedAt)[0];

        return activeEntry?.title;
    }, [entries]);

    useEffect(() => {
        document.title = formatTitle(activeTitle);
    }, [activeTitle]);

    const value = useMemo(
        () => ({
            setTitle,
            clearTitle,
        }),
        [clearTitle, setTitle]
    );

    return (
        <PageTitleContext.Provider value={value}>
            {children}
        </PageTitleContext.Provider>
    );
};

export type ManagedPageTitleOptions = {
    id?: string;
    title?: string;
    enabled?: boolean;
    priority?: number;
};

export const useManagedPageTitle = ({
    id,
    title,
    enabled = true,
    priority = 0,
}: ManagedPageTitleOptions) => {
    const generatedIdRef = useRef(`page-title-${Math.random().toString(36).slice(2)}`);
    const context = useContext(PageTitleContext);
    const titleId = id || generatedIdRef.current;

    useEffect(() => {
        if (!context) {
            if (enabled && title?.trim()) {
                document.title = formatTitle(title);
            }
            return;
        }

        if (!enabled || !title?.trim()) {
            context.clearTitle(titleId);
            return;
        }

        context.setTitle({
            id: titleId,
            title,
            priority,
        });

        return () => {
            context.clearTitle(titleId);
        };
    }, [context, enabled, priority, title, titleId]);
};
